import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationVehiclesQuery } from "../../api/network/adminApiServices";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../api/store/commonSlice";
import { TLegacyGroup } from "../../api/types/Group";
import {
  APP_CONFIG,
  REPORT_FILTER_BY_OPTION_VALUES,
} from "../../constants/constants";
import { useHeatmapContext } from "../../pages/map-heat/context/heatmap.context";
import { useOverviewContext } from "../../pages/overview/context/overview.context";
import { useLoggedInUserData } from "../../utils/user";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldCheckboxWithOnOffLabels,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldSubmit,
  PillItem,
} from "../admin/formFields";
import Pagination, { TPaginationSelected } from "../pagination";
import AppSearchBox from "../searchBox";
import AssetsSelector from "./AssetsSelector";
import FTModal from "./FTModal";
import { TGroupSelectorItem } from "./groupSelector.type";
import HierarchyGroupSelector from "./hierarchyGroupSelector";
import { consistentDisplayAssetItem } from "../../utils/vehicle";

interface ModalGroupSelectorProps {
  hideGroupAssetToggle: boolean;
  allAssetsOptionEnabled?: boolean;
  allGroups: TLegacyGroup[];
  allGroupsOptionEnabled?: boolean;
  filteredGroupData: TGroupSelectorItem[];
  restrictAssetsToThoseUnderAllGroupItems?: boolean;
  setFilteredGroupData: (groupData: TGroupSelectorItem[]) => void;
  whichContext?: "overview" | "heatmap";
}

interface CheckboxData {
  id: number;
  label: string;
  level: string;
  checked: boolean;
}

const areCheckboxDataEqual = (a: CheckboxData[], b: CheckboxData[]) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (
      a[i].id !== b[i].id ||
      a[i].label !== b[i].label ||
      a[i].level !== b[i].level ||
      a[i].checked !== b[i].checked
    ) {
      return false;
    }
  }
  return true;
};

const debugComponent = false;

const ModalGroupSelector: FC<ModalGroupSelectorProps> = ({
  hideGroupAssetToggle = false,
  allAssetsOptionEnabled = true,
  allGroups,
  allGroupsOptionEnabled = true,
  filteredGroupData,
  restrictAssetsToThoseUnderAllGroupItems = false,
  setFilteredGroupData,
  whichContext = "overview",
}) => {
  const {
    selectedGroups,
    selectedDataType,
    setSelectedDataType,
    allGroupsSelected,
    setAllGroupsSelected,
    allAssetsSelected,
    setAllAssetsSelected,
    selectedAssets,
    assetSearchKeyword,
    setAssetSearchKeyword,
    setSelectedAssets,
  } = (whichContext === "heatmap" ? useHeatmapContext : useOverviewContext)();
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "mapFleet.groupSelector",
  });

  // what the user last confirmed; survives parent re-renders and resets
  const committedGroupsRef = useRef<TGroupSelectorItem[] | null>(null);
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  const [groupSearchInput, setGroupSearchInput] = useState("");
  const [tempAllGroupSelected, setTempAllGroupSelected] = useState<boolean>(
    !!allGroupsOptionEnabled &&
      filteredGroupData.length > 0 &&
      filteredGroupData.every((group) => group.checked)
  );
  const [tempAllAssetSelected, setTempAllAssetSelected] = useState<boolean>(
    !!allAssetsOptionEnabled && allAssetsSelected
  );
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  useEffect(() => {
    if (!modalsState.showGroupSelector) {
      setGroupSearchInput("");
      setCurrentPage(1);
    }
  }, [modalsState.showGroupSelector]);
  const [groupAssetsToggled, setGroupAssetsToggled] = useState(false);
  const [checkedAssetsState, setCheckedAssetsState] = useState<{
    [key: number]: boolean;
  }>({});
  const dispatch = useDispatch();
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { vehicles: orgVehiclesQueryParams } = listingQueryParams;

  // check if group hierarchy is enabled
  // Update 8/1/2025 - for heatmap where restriction is applied, as we have specific groups to select from, we should disable the hierarchy selector
  const accountHasGroupHierarchyEnabledValue =
    useLoggedInUserData("groupHierarchy");
  const accountHasGroupHierarchyEnabled =
    restrictAssetsToThoseUnderAllGroupItems
      ? "0"
      : accountHasGroupHierarchyEnabledValue;

  const {
    data: dataOrgVehicles,
    isFetching: isFetchingOrgVehicles,
    error,
  } = useOrganizationVehiclesQuery({
    ...orgVehiclesQueryParams,
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
    group_ids: restrictAssetsToThoseUnderAllGroupItems
      ? allGroups.map((grpItem) => grpItem.groupid).join(",")
      : undefined,
  });

  const { count, next, previous, results } = dataOrgVehicles || {};
  const allAssets = results || [];
  const [assetsData, setAssetsData] = useState<CheckboxData[]>([]);

  useEffect(() => {
    // the condition wrap removed to ensure that assetsData is
    // set to empty when there are no assets as search results
    // if (allAssets && allAssets.length > 0) {}

    const updatedAssetsData = allAssets.map((asset: any) => ({
      id: asset.deviceid,
      label: consistentDisplayAssetItem(
        asset.deviceid,
        asset.description,
        asset.displayname,
        asset.drivername,
        showDriver
      ),
      // label: `${asset.deviceid}${asset.description ? ` | ${asset.description}` : ""}${asset.displayname ? ` | ${asset.displayname}` : ""}`,
      level: asset.level || "",
      checked: !!tempAllAssetSelected
        ? true
        : checkedAssetsState[asset.deviceid] || false,
    }));
    setAssetsData((prevAssetsData) =>
      areCheckboxDataEqual(prevAssetsData, updatedAssetsData)
        ? prevAssetsData
        : updatedAssetsData
    );
  }, [allAssets, checkedAssetsState, showDriver, tempAllAssetSelected]);

  // 01/23/2025 disabled this behaviour because there could be more assets on next page which are not checked
  // thus if all items on a page are checked, that doesn't mean that 'Select All' should be checked
  // useEffect(() => {
  //   const allChecked = assetsData.every((item) => item.checked);
  //   setSelectAll(allChecked);
  // }, [assetsData]);

  const handleCheckboxChange = (id: number) => {
    setCheckedAssetsState((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };
  const handleSelectAllAssetsChange = () => {
    //const newSelectAll = !allAssetsSelected;
    const newSelectAll = !tempAllAssetSelected && allAssetsOptionEnabled;
    //setAllAssetsSelected(newSelectAll);
    setTempAllAssetSelected(newSelectAll);

    setCheckedAssetsState((prevState) => {
      const updatedState: any = {};
      allAssets.forEach((asset: any) => {
        updatedState[asset.deviceid] = newSelectAll;
      });
      return updatedState;
    });
  };
  const [isModified, setIsModified] = useState<boolean>(false);
  const [tempSelected, setTempSelected] =
    useState<REPORT_FILTER_BY_OPTION_VALUES>(
      REPORT_FILTER_BY_OPTION_VALUES.GROUP
    );
  const [tempFilteredGroupData, setTempFilteredGroupData] = useState<
    TGroupSelectorItem[]
  >(
    allGroups.map((group) => ({
      ...group,
      is_hgroup: false,
      checked: true,
    }))
  );

  const handleSubmit = () => {
    setSelectedDataType(tempSelected);
    setAllAssetsSelected(tempAllAssetSelected);
    setAllGroupsSelected(tempAllGroupSelected);
    // if (selectedDataType === REPORT_FILTER_BY_OPTION_VALUES.ASSET) {
    if (tempSelected === REPORT_FILTER_BY_OPTION_VALUES.ASSET) {
      if (Object.keys(checkedAssetsState).length === 0) return;
      const selectedAssetIds = Object.keys(checkedAssetsState)
        .filter((key: any) => checkedAssetsState[key])
        .map((key) => key);
      setSelectedAssets(selectedAssetIds);
    } else {
      if (
        tempFilteredGroupData.filter((item) => item.checked).length === 0 &&
        // !allGroupsSelected
        !tempAllGroupSelected
      )
        return;
      setFilteredGroupData(tempFilteredGroupData);
      committedGroupsRef.current = tempFilteredGroupData;
      const selectedGroupData = tempFilteredGroupData
        .filter((item) => item.checked)
        .map((group) => group.groupid);
      setSelectedAssets(selectedGroupData);
    }
    setTimeout(() => hideModal(), 300);
  };
  const searchKeyword = groupSearchInput.trim().toLowerCase();
  const searchedGroupData = tempFilteredGroupData.filter((group) =>
    [group.groupid, group.description].some((value) =>
      String(value ?? "").toLowerCase().includes(searchKeyword)
    )
  )
  const paginatedData = searchedGroupData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handlePageChange = (data: TPaginationSelected) => {
    setCurrentPage(data.selected + 1); // Update the current page
  };

  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (debugComponent)
      console.log("[UPDATE]", "setting on change of filteredGroupData");
    // disabled the following line as it was preventing all checkboxes from being checked/unchecked when 'Select All' is clicked
    // setTempFilteredGroupData(filteredGroupData);
    if (!isModified) {
      setTempFilteredGroupData(filteredGroupData);
      setTempAllGroupSelected(
        !!allGroupsOptionEnabled &&
          filteredGroupData.length > 0 &&
          filteredGroupData.every((group) => group.checked)
      );
    }
  }, [filteredGroupData, isModified, allGroupsOptionEnabled]);

  const handleAllCheckboxChange = (e: any) => {
    // setAllGroupsSelected(e.target.checked);
    setTempAllGroupSelected(e.target.checked);
    // disabled the line below to allow checkboxes to sync with 'Select All' checkbox
    // if (!e.target.checked) return; // on if checked, select all groups on page
    if (debugComponent)
      console.log("[UPDATE]", "setting on change of all checkbox");
    // Update the `checked` status for all groups
    setTempFilteredGroupData(
      tempFilteredGroupData.map((group) => ({
        ...group,
        checked: e.target.checked, // True or false based on the checkbox status
      }))
    );
    setIsModified(true);
    if (debugComponent) {
      console.log(
        "[UPDATE]",
        `Setting all groups to ${e.target.checked ? "selected" : "deselected"}`
      );
    }
  };

  const hideModal = () => {
    if (groupAssetsToggled) {
      if (
        Object.values(checkedAssetsState).filter((item: boolean) => item)
          .length === 0 &&
        !tempAllAssetSelected
      ) {
        toast.error(t("atleast_one_asset"));
        return;
      }
    } else {
      if (
        tempFilteredGroupData.filter((item) => item.checked).length === 0 &&
        !tempAllGroupSelected
      ) {
        toast.error(t("atleast_one_group"));
        return;
      }
    }
    dispatch(setModalsData({ ...modalsState, showGroupSelector: false }));
  };

  const handleGroupSearch = (searchTerm: string) => {
    const normalizedSearchTerm = searchTerm === "clear" ? "" : searchTerm;
    setGroupSearchInput(normalizedSearchTerm);
    setCurrentPage(1);
  };

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    // set search term
    setAssetSearchKeyword(value);
    //setAllAssetsSelected(value.length === 0);
    setTempAllAssetSelected(value.length === 0 && !!allAssetsOptionEnabled);
    // if search term is empty, select all assets on page
    // if (value.length === 0) {
    //   setCheckedAssetsState((prevState) => {
    //     const updatedState: any = {};
    //     allAssets.forEach((asset: any) => {
    //       updatedState[asset.deviceid] = true;
    //     });
    //     return updatedState;
    //   });
    //   const updatedAssetsData = allAssets.map((asset: any) => ({
    //     id: asset.deviceid,
    //     label: asset.displayname,
    //     level: asset.level || "",
    //     checked: true,
    //   }));
    //   setAssetsData(updatedAssetsData);
    // }

    if (value.length > 0) {
      setTempAllAssetSelected(false);
      setCheckedAssetsState((prevState) => {
        const updatedState: any = {};
        allAssets.forEach((asset: any) => {
          updatedState[asset.deviceid] = false;
        });
        return updatedState;
      });
      const updatedAssetsData = allAssets.map((asset: any) => ({
        id: asset.deviceid,
        label: asset.displayname,
        level: asset.level || "",
        checked: false,
      }));
      setAssetsData((prevAssetsData) =>
        areCheckboxDataEqual(prevAssetsData, updatedAssetsData)
          ? prevAssetsData
          : updatedAssetsData
      );
    }
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        vehicles: { ...orgVehiclesQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  useEffect(() => {
    if (assetSearchKeyword.length === 0) {
      //if search term is empty, select all assets on page
      setTempAllAssetSelected(allAssetsOptionEnabled); // true
      setAllAssetsSelected(true);
      if (APP_CONFIG.DEBUG.ALL)
        console.log("setting all assets selected", assetSearchKeyword);
      setCheckedAssetsState((prevState) => {
        const updatedState: any = {};
        allAssets.forEach((asset: any) => {
          updatedState[asset.deviceid] = true;
        });
        return updatedState;
      });
    }
  }, [assetSearchKeyword.length === 0]);

  const handleTypeToggleChange = () => {
    if (groupAssetsToggled) {
      setGroupAssetsToggled(false);
      //setSelectedDataType(REPORT_FILTER_BY_OPTION_VALUES.GROUP);
      setTempSelected(REPORT_FILTER_BY_OPTION_VALUES.GROUP);
    } else {
      setGroupAssetsToggled(true);
      //setSelectedDataType(REPORT_FILTER_BY_OPTION_VALUES.ASSET);
      setTempSelected(REPORT_FILTER_BY_OPTION_VALUES.ASSET);
    }
  };

  useEffect(() => {
    //handleSelectAllAssetsChange();
    debouncedSetSearchKeyword("");
  }, [groupAssetsToggled, orgVehiclesQueryParams.page_size]);

  // disable all groups selected if all groups option is disabled
  useEffect(() => {
    if (!allGroupsOptionEnabled) {
      setAllGroupsSelected(false);
    }
    return () => {
      setAllGroupsSelected(true);
    };
  }, []);

  // unmount cleanup all states when modal is used from different places/components/routes
  useEffect(() => {
    return () => {
      if (APP_CONFIG.DEBUG.ALL) console.warn("modalGroupSelector UNMOUNTED");
      setTempFilteredGroupData(
        allGroups.map((group) => ({
          ...group,
          is_hgroup: false,
          checked: true,
        }))
      );
      setTempSelected(REPORT_FILTER_BY_OPTION_VALUES.GROUP);
      setGroupAssetsToggled(false);
      setGroupSearchInput("");
      setAssetSearchKeyword("");
      // following line commented as to maintain all groups selected state
      // setAllGroupsSelected(false);
      setAllAssetsSelected(false);
      setCheckedAssetsState({});
      setCurrentPage(1);
      setIsModified(false);
    };
  }, []);

  return (
    <>
      <FTModal
        visible={modalsState.showGroupSelector}
        heading={!groupAssetsToggled ? t("heading") : t("heading_assets")}
        onHide={hideModal}
        containerStyle="max-w-[calc(100vw-4rem)] w-[470px]"
        dismissible={true}
      >
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="text-[8px]">
            allGroupsSelected: {allGroupsSelected.toString()} /
            tempAllGroupSelected: {tempAllGroupSelected.toString()}
            <br />
            allAssetsSelected: {allAssetsSelected.toString()} /
            tempAllAssetSelected: {tempAllAssetSelected.toString()}
            <br />
          </pre>
        )}
        <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
          <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
            {t(!groupAssetsToggled ? "sub_heading_group" : "sub_heading_asset")}
          </p>
          <div className="flex justify-between col-span-6 gap-4">
            {!groupAssetsToggled ? (
              <>
                {accountHasGroupHierarchyEnabled === "1" ? (
                  <span className="flex-grow"></span>
                ) : (
                  <AppSearchBox
                    wrapperClassName="flex-grow"
                    placeholder={tMain("search_groups")}
                    controlledValue={groupSearchInput}
                    onChange={(e) => handleGroupSearch(e.target.value)}
                    onClear={() => handleGroupSearch("clear")}
                  />
                )}
              </>
            ) : (
              <AppSearchBox
                wrapperClassName="flex-grow"
                placeholder={tMain("search_assets")}
                initialValue={orgVehiclesQueryParams.search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  debouncedSetSearchKeyword(e.target.value)
                }
                onClear={() => debouncedSetSearchKeyword("")}
                loading={isFetchingOrgVehicles}
              />
            )}
            {!hideGroupAssetToggle && (
              <AdminFormFieldCheckboxWithOnOffLabels
                // customWrapperClass="w-40"
                offLabel={tMain("group_toggle_label_on")}
                onLabel={tMain("group_toggle_label_off")}
                label=""
                id="time_toggle"
                type="checkbox"
                name="time_toggle"
                onChange={handleTypeToggleChange}
                checked={groupAssetsToggled}
              />
            )}
          </div>
          {!groupAssetsToggled ? (
            <div
              className={`relative col-span-6 ${accountHasGroupHierarchyEnabled === "1" && !groupAssetsToggled ? "h-[calc(100vh-480px)]" : "h-[calc(100vh-480px)]"} overflow-y-auto`}
            >
              <div className="absolute w-full h-full overflow-auto grid grid-cols-6 gap-2 content-start">
                <div className="col-span-6 sticky top-0 bg-white z-[11]">
                  {!!allGroupsOptionEnabled && (
                    <AdminFormFieldCheckbox
                      id="all"
                      label="Select All"
                      type="checkbox"
                      name="all"
                      // checked={allGroupsSelected}
                      checked={tempAllGroupSelected}
                      onChange={handleAllCheckboxChange}
                      disabled={!allGroupsOptionEnabled || !!groupSearchInput}
                      // key={`all_groups_${allGroupsSelected.toString()}`}
                      key={`all_groups_${tempAllGroupSelected.toString()}`}
                    />
                  )}
                </div>

                <React.Fragment
                  // key={`groups_list_${allGroupsSelected.toString()}`}
                  key={`groups_list_${tempAllGroupSelected.toString()}`}
                >
                  {accountHasGroupHierarchyEnabled === "1" ? (
                    <>
                      <div className="sticky col-span-6 top-16 z-10">
                        <span className="absolute w-full h-16 bg-white -top-2 -mt-[3px] z-10" />
                        <AdminFormFieldSubmit
                          label={tMain("select_groups_button")}
                          type="button"
                          variant="primary-like"
                          customWrapperClass="w-full relative z-10"
                          onClick={() => {
                            dispatch(
                              setModalsData({
                                ...modalsState,
                                showHierarchyGroupSelector: true,
                              })
                            );
                          }}
                          // disabled={!!allGroupsSelected} // disable if user cannot edit or all groups is selected
                          disabled={!!tempAllGroupSelected}
                        />
                      </div>
                      <div className="col-span-6 sticky top-[61px] z-10 grid grid-cols-6">
                        {/* modal moved below out of this FTModal to fix z-index issue */}
                      </div>
                      {/* <pre className="col-span-6 text-[8px] h-24 overflow-auto">
                      tempFilteredGroupData (truncated):
                      {JSON.stringify(
                        tempFilteredGroupData.map((item) => item.groupid),
                        null,
                        2
                      )}
                    </pre> */}
                      <div className="col-span-6">
                        <AdminFormFieldPillList
                          // disabled={!!allGroupsSelected}
                          disabled={!!tempAllGroupSelected}
                          // isError={errors.selected_groups && userCanEdit}
                        >
                          {tempFilteredGroupData
                            .filter((item) => item.checked)
                            .map((group: TGroupSelectorItem, i: number) => (
                              <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                                key={`pillItem_${i}`}
                                // disabled={!!allGroupsSelected}
                                disabled={!!tempAllGroupSelected}
                                item={{ name: group.groupid } as PillItem}
                                onRemove={() => {
                                  console.log(
                                    "[UPDATE]",
                                    "setting on remove pill item"
                                  );
                                  setTempFilteredGroupData(
                                    tempFilteredGroupData.map((grp) => {
                                      if (grp.groupid === group.groupid) {
                                        return { ...grp, checked: false };
                                      }
                                      return grp;
                                    })
                                  );
                                }}
                                // onItemClick={(item) => {
                                //   dispatch(
                                //     setModalsData({
                                //       ...modalsState,
                                //       showReadOnlyViewForHierarchyGroup: {
                                //         accountid: thisUserOrganizationId,
                                //         active_inactive_vehicle_counts: {
                                //           active_vehicle_count: 0,
                                //           inactive_vehicle_count: 0,
                                //         },
                                //         description: "",
                                //         groupid: group,
                                //       } as OrgGHGroup,
                                //       showHierarchyGroupSelector: true,
                                //     })
                                //   );
                                // }}
                              />
                            ))}
                        </AdminFormFieldPillList>
                      </div>
                    </>
                  ) : (
                    <>
                      {paginatedData.map(
                        (group: TGroupSelectorItem, index: number) => (
                          <div className="col-span-6" key={index}>
                            <AdminFormFieldCheckbox
                              id={group.groupid}
                              label={group.description ? `${group.groupid} | ${group.description}` : group.groupid}
                              type="checkbox"
                              name={group.groupid}
                              checked={group.checked}
                              onChange={(e) => {
                                const updatedGroups = tempFilteredGroupData.map(
                                  (grp) =>
                                    grp.groupid === group.groupid
                                      ? {
                                          ...grp,
                                          checked: e.target.checked,
                                        }
                                      : grp
                                );
                                setTempFilteredGroupData(updatedGroups);
                                setIsModified(true);
                                setTempAllGroupSelected(
                                  !!allGroupsOptionEnabled &&
                                    !groupSearchInput &&
                                    updatedGroups.length > 0 &&
                                    updatedGroups.every((item) => item.checked)
                                );
                              }}
                            />
                          </div>
                        )
                      )}
                    </>
                  )}
                </React.Fragment>
              </div>
            </div>
          ) : (
            <AssetsSelector
              data={assetsData}
              // selectAll={allAssetsSelected}
              selectAll={tempAllAssetSelected}
              handleSelectAllChange={handleSelectAllAssetsChange}
              handleCheckboxChange={handleCheckboxChange}
              assetSearchKeyword={assetSearchKeyword}
              setAssetSearchKeyword={setAssetSearchKeyword}
              loading={isFetchingOrgVehicles}
              allAssetsOptionEnabled={allAssetsOptionEnabled}
              selectorItemTitleFormatter={(item: any) => item.label}
            />
          )}
          {(accountHasGroupHierarchyEnabled !== "1" || groupAssetsToggled) && (
            <>
              {!groupAssetsToggled ? (
                <Pagination
                  pageSize={PAGE_SIZE}
                  totalPages={Math.max(
                    1,
                    Math.ceil(searchedGroupData.length / PAGE_SIZE)
                  )}
                  forcePage={currentPage - 1}
                  handlePageClick={handlePageChange}
                  onlyPageChange={true}
                  size={1}
                  wrapperClass="col-span-6"
                />
              ) : (
                <Pagination
                  pageSize={orgVehiclesQueryParams.page_size}
                  onlyPageChange={true}
                  totalPages={
                    count
                      ? Math.ceil(count / orgVehiclesQueryParams.page_size)
                      : 1
                  }
                  forcePage={orgVehiclesQueryParams.page - 1}
                  handlePageClick={(data: TPaginationSelected) => {
                    dispatch(
                      setListingQueryParams({
                        ...listingQueryParams,
                        vehicles: {
                          ...orgVehiclesQueryParams,
                          page: data?.selected + 1,
                        },
                      })
                    );
                  }}
                  paginCurrentPage={orgVehiclesQueryParams.page}
                  paginItemsOnCurrentPage={results?.length || 0}
                  paginTotalItemsCount={count}
                  size={1}
                  wrapperClass="col-span-6"
                />
              )}
            </>
          )}
          {!groupAssetsToggled ? (
            <>
              <AdminFormFieldSubmit
                label={
                  tempFilteredGroupData.filter((item) => item.checked)
                    .length ===
                    // 0 && !allGroupsSelected
                    0 && !tempAllGroupSelected
                    ? t("atleast_one_group")
                    : tMain("confirm")
                }
                type="button"
                variant={
                  tempFilteredGroupData.filter((item) => item.checked)
                    .length ===
                    // 0 && !allGroupsSelected
                    0 && !tempAllGroupSelected
                    ? "danger"
                    : "primary"
                }
                onClick={handleSubmit}
                disabled={
                  tempFilteredGroupData.filter((item) => item.checked)
                    .length ===
                    // 0 && !allGroupsSelected
                    0 && !tempAllGroupSelected
                }
              />

              <AdminFormFieldSubmit
                customWrapperClass={`col-span-6${tempFilteredGroupData.filter((item) => item.checked).length === 0 ? " hidden" : ""}`}
                label={tMain("cancel")}
                type="button"
                variant="danger-transparent"
                onClick={() => {
                  // last confirmed groups, or everything selected if none confirmed
                  const restored =
                    committedGroupsRef.current ??
                    allGroups.map((group) => ({
                      ...group,
                      is_hgroup: false,
                      checked: true,
                    }));
                  setTempFilteredGroupData(restored);
                  setTempAllGroupSelected(
                    !!allGroupsOptionEnabled &&
                      restored.length > 0 &&
                      restored.every((group) => group.checked)
                  );
                  dispatch(
                    setModalsData({ ...modalsState, showGroupSelector: false })
                  );
                }}
              />
            </>
          ) : (
            <>
              {false && (
                <pre className="text-[8px]">
                  {JSON.stringify(
                    Object.values(checkedAssetsState).filter(
                      (item: boolean) => item
                    ),
                    null,
                    2
                  )}
                  <br />
                  ========
                  <br />
                  tempAllAssetSelected: {JSON.stringify(tempAllAssetSelected)}
                  <br />
                  ========
                  <br />
                  !tempAllAssetSelected: {JSON.stringify(!tempAllAssetSelected)}
                  <br />
                  ========
                  <br />
                  allAssetsOptionEnabled:{" "}
                  {JSON.stringify(allAssetsOptionEnabled)}
                </pre>
              )}
              <AdminFormFieldSubmit
                label={
                  Object.values(checkedAssetsState).filter(
                    (item: boolean) => item
                    // ).length === 0 && !allAssetsSelected
                  ).length === 0 && !tempAllAssetSelected
                    ? t("atleast_one_asset")
                    : tMain("confirm")
                }
                type="button"
                variant={
                  Object.values(checkedAssetsState).filter(
                    (item: boolean) => item
                    // ).length === 0 && !allAssetsSelected
                  ).length === 0 && !tempAllAssetSelected
                    ? "danger"
                    : "primary"
                }
                onClick={handleSubmit}
                disabled={
                  Object.values(checkedAssetsState).filter(
                    (item: boolean) => item
                    // ).length === 0 && !allAssetsSelected
                  ).length === 0 && !tempAllAssetSelected
                }
              />

              <AdminFormFieldSubmit
                customWrapperClass={`col-span-6${Object.values(checkedAssetsState).filter((item: boolean) => item).length === 0 ? " hidden" : ""}`}
                label={tMain("cancel")}
                type="button"
                variant="danger-transparent"
                onClick={hideModal}
              />
            </>
          )}
        </form>
      </FTModal>

      {/* Hierarchy Group Selector - separate modal */}
      {accountHasGroupHierarchyEnabled === "1" && (
        <>
          {modalsState.showHierarchyGroupSelector && (
            <HierarchyGroupSelector
              initiallySelectedGroups={tempFilteredGroupData
                ?.filter((item) => item.checked)
                .map((item) => item.groupid)}
              onSelectionChange={(selectedGroups: string[]) => {
                if (APP_CONFIG.DEBUG.DISPLAY_VERBOSE)
                  console.log("VALUES RECEIVED:", selectedGroups);
                console.log(
                  "GROUP",
                  selectedGroups.map((grp) => {
                    return {
                      groupid: grp,
                      description: "",
                      checked: true,
                      is_hgroup: true,
                    };
                  })
                );
                if (debugComponent)
                  console.log(
                    "[UPDATE]",
                    "setting on hierarchy group selection"
                  );
                setTempFilteredGroupData((prev) => {
                  let selected = [...selectedGroups];
                  const updated = prev.map((item) => {
                    const isAvailable = selectedGroups.includes(item?.groupid);
                    if (isAvailable) {
                      selected = selected.filter((val) => val != item.groupid);
                      return {
                        groupid: item?.groupid,
                        description: item?.description,
                        checked: true,
                        is_hgroup: true,
                      };
                    }
                    return {
                      groupid: item?.groupid,
                      description: item?.description,
                      checked: false,
                      is_hgroup: item?.is_hgroup,
                    };
                  });
                  const newSelected = selected.map((grp) => {
                    return {
                      groupid: grp,
                      description: "",
                      checked: true,
                      is_hgroup: true,
                    };
                  });
                  return [...updated, ...newSelected];
                });
                // setTempFilteredGroupData(
                //   selectedGroups.map((grp) => {
                //     return {
                //       groupid: grp,
                //       checked: true,
                //       is_hgroup: true,
                //     };
                //   })
                // );
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default ModalGroupSelector;
